import { useEffect, useRef, useState, useCallback } from "react";

function isLikelyDesktop() {
  // Not perfect, but good enough
  const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
  const wide = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px)").matches;
  return !hasTouch && wide;
}

/**
 * Autoplay with sound if allowed.
 * If blocked, autoplay muted and show "enable sound" overlay.
 */
export function useAutoplayOnceVideo() {
  const videoRef = useRef(null);

  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [startedMuted, setStartedMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const tryPlayUnmuted = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return false;

    v.muted = false;
    v.volume = 1;

    try {
      await v.play();
      setIsPlaying(true);
      setNeedsUserGesture(false);
      setStartedMuted(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  const tryPlayMuted = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return false;

    v.muted = true;
    v.volume = 0;

    try {
      await v.play();
      setIsPlaying(true);

      // It started, but audio needs user gesture later
      setStartedMuted(true);
      setNeedsUserGesture(true);
      return true;
    } catch {
      setIsPlaying(false);
      setStartedMuted(false);
      setNeedsUserGesture(true);
      return false;
    }
  }, []);

  const tryAutoplay = useCallback(async () => {
    const okAudio = await tryPlayUnmuted();
    if (okAudio) return;

    const okMuted = await tryPlayMuted();
    if (!okMuted) return;

    // Optional desktop retry: sometimes works after decode/initial playback
    if (isLikelyDesktop()) {
      setTimeout(async () => {
        // Only retry if we’re still in muted-start state
        const v = videoRef.current;
        if (!v) return;
        if (!startedMuted) return;

        const ok = await tryPlayUnmuted();
        if (ok) {
          // If unmuted succeeds, clear overlays
          setStartedMuted(false);
          setNeedsUserGesture(false);
        }
      }, 600);
    }
  }, [tryPlayUnmuted, tryPlayMuted, startedMuted]);

  const enableAudio = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = false;
    v.volume = 1;

    try {
      // If it was playing muted, force audio to attach reliably
      if (!v.paused) v.pause();
      await v.play();

      setIsPlaying(true);
      setNeedsUserGesture(false);
      setStartedMuted(false);
    } catch {
      setIsPlaying(false);
      setNeedsUserGesture(true);
      // keep startedMuted as-is
    }
  }, []);

  const togglePlayPause = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      // If we started muted, on user gesture we prefer enabling audio
      if (startedMuted || needsUserGesture) {
        await enableAudio();
        return;
      }

      // Otherwise normal play with audio
      const ok = await tryPlayUnmuted();
      if (!ok) {
        // last fallback (should be rare after user click)
        await enableAudio();
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, [enableAudio, needsUserGesture, startedMuted, tryPlayUnmuted]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);

    tryAutoplay();

    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [tryAutoplay]);

  return {
    videoRef,
    needsUserGesture,
    startedMuted,
    isPlaying,
    enableAudio,
    togglePlayPause
  };
}
